
import AdUnit from '../components/AdUnit';
import AffiliateBlock from '../components/AffiliateBlock';
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, BedDouble, ArrowLeft, ExternalLink, Star } from 'lucide-react';
import { PageHero } from '../components/shared';
import SaveButton from '../components/SaveButton';
import { imgAt } from '../mock';

const BACKEND = (typeof window !== 'undefined' ? window.location.origin : '') + '/api';

export default function HotelDetail() {
  const { slug } = useParams();
  const [hotel, setHotel] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    setState('loading');
    fetch(`${BACKEND}/hotels/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((h) => { setHotel(h); setState('ok'); document.title = `${h.name}, ${h.emirate} — Emirates Guide`; })
      .catch(() => setState('missing'));
  }, [slug]);

  if (state === 'loading') return <div className="min-h-[60vh] flex items-center justify-center text-[#4B4F58]">Loading…</div>;
  if (state === 'missing') return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-6">
      <h1 className="font-display text-3xl font-semibold text-[#1B1D21]">We couldn't find that hotel</h1>
      <Link to="/hotels" className="btn-gold">Browse all hotels</Link>
    </div>
  );

  const h = hotel;
  const bookingUrl = `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(h.name + ' ' + h.emirate)}`;

  return (
    <div className="bg-[#F6F1E3]">
      <PageHero image={h.image} label={`${h.area} · ${h.emirate}`} title={h.name} subtitle={h.desc} />
      <div className="max-w-5xl mx-auto px-5 py-10 md:py-14 grid md:grid-cols-[1fr_320px] gap-10">
        <article>
          <Link to="/hotels" className="inline-flex items-center gap-1.5 text-[13px] text-[#8a5a2e] hover:text-[#B68A35]"><ArrowLeft className="w-4 h-4" /> All hotels</Link>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[13px]">
            <span className="inline-flex items-center gap-1 rounded-full bg-white border border-[#E6D7A2] px-3 py-1 text-[#2F663C] font-medium"><MapPin className="w-3.5 h-3.5" /> {h.area}, {h.emirate}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white border border-[#E6D7A2] px-3 py-1 text-[#8a5a2e]"><BedDouble className="w-3.5 h-3.5" /> {h.budgetLabel}</span>
            {h.featured ? <span className="inline-flex items-center gap-1 rounded-full bg-[#CBA344] px-3 py-1 text-[#1B1D21] font-semibold"><Star className="w-3.5 h-3.5" /> Editor's pick</span> : null}
          </div>
          <div className="prose-aue mt-6 space-y-4 text-[16px] leading-relaxed text-[#2A2C31]">
            {(h.body || [h.desc]).map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <p className="mt-8 text-[12px] text-[#6B6F78]">Independent guide. Rates shown are indicative; the booking partner shows the live price. We may earn a commission if you book through our links — it never changes the price you pay.</p>
          <AffiliateBlock placement="hotels" title="Compare prices for this stay" columns={2} className="mt-6" />
          <AdUnit slot="footer" />
        </article>
        <aside className="md:sticky md:top-24 self-start">
          <div className="bg-white rounded-2xl border border-[#E6D7A2] p-6 shadow-[0_18px_40px_-28px_rgba(27,29,33,0.45)]">
            <div className="text-[13px] text-[#6B6F78]">Typical rate</div>
            <div className="font-display text-[28px] font-semibold text-[#1B1D21]">{h.priceLabel}</div>
            <a href={bookingUrl} target="_blank" rel="noopener sponsored" className="btn-gold mt-5 w-full justify-center">Check availability <ExternalLink className="w-4 h-4" /></a>
            <div className="mt-3 flex justify-center"><SaveButton item={{ type: 'hotel', ref_id: `hotel-${h.id}`, title: h.name, subtitle: `${h.area}, ${h.emirate}`, to: `/hotels/${h.slug}` }} /></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
